import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import fplTokens from '@figma/fpl-tokens/css-vars';
import { borderRadius, color } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { ButtonPrimitive } from '@figma/fpl-components';
import { Icon16ChevronDown, Icon24Diagram, Icon24Library, Icon24Sun } from '@figma/fpl-icons';
import { Box } from '@/UI/Box/Box';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { DrawerUserAvatar } from '@/components/Dashboard/DrawerUserAvatar';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { Drawer } from '@/UI/Drawer/Drawer';
import { List } from '@/UI/List/List';
import { NavigationListItem } from '@/UI/List/NavigationListItem';
import { AuthContext } from '@/contexts/AuthContext';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useIsThemeModeSelectable } from '@/contexts/ThemeModeContext';
import { I18N_KEYS } from '@/language/keys';
import { SubscriptionType } from '@/types/shared';
import { FlexCenHorVer, FlexCenVer, FlexCol } from '@/UI/styles';
import { SettingsPageSections } from '@/enums/settings-page-sections.enum';
import { CaretIcon } from '@/UI/Icons/CaretIcon';
import { DiscordLogo } from '@/UI/Icons/DiscordLogo';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AppToggleButton } from '@/UI/AppToggleButtons/AppToggleButton';
import { PlusIcon } from '@/UI/Icons/PlusIcon';
import { FolderEmptyIcon } from '@/UI/Icons/FolderEmptyIcon';
import { Cards3Icon } from '@/UI/Icons/Cards3Icon';
import { AppWindowIcon } from '@/UI/Icons/AppWindowIcon';
import { TeamIcon } from '@/UI/Icons/TeamIcon';
import { DashboardSection } from '@/components/Dashboard/enums/shared';
import { isFileScopeSection, type DashboardPage } from '@/components/Dashboard/utils/dashboardSection.utils';
import { useToolTabsEnabled } from '@/hooks/useToolTabsEnabled';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_TOOLS_CATALOG } from '@/consts/featureFlags';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { UsersIcon } from '@/UI/Icons/UsersIcon';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { FileIcon } from '@/UI/Icons/FileIcon';
import { FolderScope } from '@/enums/folder-scope.enum';
import { useBanner, APP_BANNER_ID } from '@/hooks/useBanner';
import { useCreateFolder } from '@/queries/useFolders';
import { useCreateRecipe } from '@/queries/useRecipes';
import { SettingsIcon, SignOutIcon, TrashIcon } from '@/UI/Icons';
import { QuestionMarkIcon } from '@/UI/Icons/QuestionMarkIcon';
import { openIntercom } from '@/providers/intercom';
import { ThemeMenuItem } from '@/components/Menu/ThemeMenuItem';
import { MenuAction } from '../Menu/Actions';
import { UserMenu } from './UserMenu/UserMenu';
import { UniversalUpgradeBadge } from './UniversalUpgradeBadge';
import { useDashboardFolders } from './useDashboardFolders';
import { useWorkspaceFolders } from './useWorkspaceFolders';
import { CreateFolderModal } from './CreateFolderModal';
import type { User } from '@/types/auth.types';

interface NavigationItem {
  label: string;
  icon: React.ReactElement;
  section: DashboardSection;
  /** Set on the Workflows and Tools rows. Selecting one keeps the current My / Workspace / Shared tab. */
  page?: DashboardPage;
  disable?: boolean;
  endIcon?: React.ReactElement;
}

interface NavigationDrawerProps {
  drawerWidth: number;
  hasWorkspaceFiles?: boolean;
  showWorkspaceFilesSection?: boolean;
  mobileOpen: boolean;
  onSectionChange: (section: DashboardSection) => void;
  /** Which kind page is open. Only read while `dashboard-tool-tabs` is on. */
  selectedPage?: DashboardPage;
  onPageChange?: (page: DashboardPage) => void;
  selectedSection: DashboardSection;
  setIsClosing: (isClosing: boolean) => void;
  setMobileOpen: (mobileOpen: boolean) => void;
  user: User;
}

/**
 * @@spec-file: ./NavigationDrawer.spec.md
 */
function NavigationDrawer({
  drawerWidth,
  hasWorkspaceFiles = false,
  showWorkspaceFilesSection = false,
  mobileOpen,
  onSectionChange,
  selectedPage = 'workflow',
  onPageChange,
  selectedSection,
  setIsClosing,
  setMobileOpen,
  user,
}: NavigationDrawerProps) {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const nav = useNavigate();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const isThemeModeSelectable = useIsThemeModeSelectable();
  const [isShowingCreateFolderDialog, setIsShowingCreateFolderDialog] = useState(false);
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const { offset } = useBanner(APP_BANNER_ID);
  const areTabsEnabled = useToolTabsEnabled();
  const { value: isToolsCatalogEnabled } = useFeatureFlag(FF_TOOLS_CATALOG);

  const { currentFolderId: personalFolderId } = useDashboardFolders();
  const { currentFolderId: workspaceFolderId } = useWorkspaceFolders();
  const { mutateAsync: createFolder } = useCreateFolder();
  const { mutateAsync: createWorkflowInScope } = useCreateRecipe();
  // The Workflows row stays selected on Workspace and Shared. Create has to follow that tab:
  // a personal folder would never show up on Workspace, and Shared is not yours to add to.
  const createsInWorkspace = areTabsEnabled && selectedSection === DashboardSection.WorkspaceFiles;
  const createParentId = createsInWorkspace ? workspaceFolderId : personalFolderId;
  const createScope = createsInWorkspace ? FolderScope.WORKSPACE : FolderScope.PERSONAL;
  const hideRowCreate = areTabsEnabled && selectedSection === DashboardSection.SharedWithMe;

  const foldersList = useMemo((): NavigationItem[] => {
    // Kind pages replace the scope rows. My / Workspace / Shared with me become tabs on each page,
    // so a workspace no longer adds a sidebar row and the old Apps row would duplicate Tools.
    const baseItems: NavigationItem[] = areTabsEnabled
      ? [
          {
            label: t(I18N_KEYS.NAVIGATION_DRAWER.WORKFLOWS),
            icon: <Icon24Diagram />,
            section: DashboardSection.RecentRecipes,
            page: 'workflow',
            endIcon: hideRowCreate ? undefined : <PlusIcon width={12} height={12} />,
          },
          {
            label: t(I18N_KEYS.NAVIGATION_DRAWER.TOOLS),
            icon: <AppWindowIcon width={20} height={20} />,
            section: DashboardSection.RecentRecipes,
            page: 'tool',
          },
        ]
      : [
          {
            label: t(I18N_KEYS.NAVIGATION_DRAWER.MY_FILES),
            icon: <Cards3Icon width={20} height={20} />,
            section: DashboardSection.RecentRecipes,
            endIcon: <PlusIcon width={12} height={12} />,
          },
          {
            label: t(I18N_KEYS.NAVIGATION_DRAWER.SHARED_WITH_ME),
            icon: <UsersIcon width={20} height={20} />,
            section: DashboardSection.SharedWithMe,
          },
        ];

    const workspaceItem: NavigationItem | null =
      !areTabsEnabled && showWorkspaceFilesSection
        ? {
            label: t(I18N_KEYS.NAVIGATION_DRAWER.WORKSPACE_FILES),
            icon: <TeamIcon width={20} height={20} />,
            section: DashboardSection.WorkspaceFiles,
            disable: false,
          }
        : null;

    const appsItem: NavigationItem | null = areTabsEnabled
      ? null
      : {
          label: t(I18N_KEYS.NAVIGATION_DRAWER.APPS),
          icon: <AppWindowIcon width={20} height={20} />,
          section: DashboardSection.SharedApps,
        };

    const toolkitItem: NavigationItem | null =
      isToolsCatalogEnabled === true
        ? {
            label: t(I18N_KEYS.NAVIGATION_DRAWER.TOOLKIT),
            icon: <Icon24Library />,
            section: DashboardSection.Toolkit,
          }
        : null;

    const archiveItem: NavigationItem = {
      label: t(I18N_KEYS.NAVIGATION_DRAWER.ARCHIVE),
      icon: <TrashIcon width={20} height={20} />,
      section: DashboardSection.Archive,
    };

    return [
      ...baseItems,
      ...(workspaceItem ? [workspaceItem] : []),
      ...(appsItem ? [appsItem] : []),
      ...(toolkitItem ? [toolkitItem] : []),
      archiveItem,
    ];
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [t, showWorkspaceFilesSection, hasWorkspaceFiles, areTabsEnabled, isToolsCatalogEnabled, hideRowCreate]);

  const { signOut } = useContext(AuthContext);
  const [anchorElUserMenu, setAnchorElUserMenu] = useState<HTMLElement | null>(null);
  const [anchorElCreateMenu, setAnchorElCreateMenu] = useState<HTMLElement | null>(null);

  const { name } = user;

  const handleListItemClick = (item: NavigationItem) => {
    if (item.page) {
      onPageChange?.(item.page);
      // Trash and Toolkit are not tabs. Opening a kind page from one of them lands on My.
      if (!isFileScopeSection(selectedSection)) {
        onSectionChange(DashboardSection.RecentRecipes);
      }
      return;
    }
    onSectionChange(item.section);
  };

  const isItemSelected = (item: NavigationItem) => {
    if (item.page) {
      return item.page === selectedPage && isFileScopeSection(selectedSection);
    }
    return item.section === selectedSection;
  };

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => setIsClosing(false);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElUserMenu(event.currentTarget);

  const handleCloseUserMenu = useCallback(() => setAnchorElUserMenu(null), []);

  const handleOpenCreateMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElCreateMenu(event.currentTarget);

  // eslint-disable-next-line react-hooks-js/exhaustive-deps
  const handleCloseCreateMenu = () => setAnchorElCreateMenu(null);

  const handleCreateFile = useCallback(() => {
    handleCloseCreateMenu();
    track('created_new_flow', { source: 'dashboard_navigation_drawer' }, TrackTypeEnum.BI);
    void createWorkflowInScope({ parentId: createParentId, scope: createScope });
  }, [createParentId, createScope, createWorkflowInScope, handleCloseCreateMenu, track]);

  const handleCreateFolder = useCallback(
    async (folderName: string) => {
      handleCloseCreateMenu();
      track('create_folder_clicked', { source: 'dashboard_navigation_drawer' }, TrackTypeEnum.BI);
      await createFolder({ name: folderName, scope: createScope, parentId: createParentId });
    },
    [createParentId, createScope, handleCloseCreateMenu, track, createFolder],
  );

  const signOutUser = useCallback(() => {
    void signOut();
  }, [signOut]);

  const menuItems = useMemo(() => {
    const res: MenuAction[] = [
      {
        type: 'divider' as const,
      },
    ];

    res.push({
      biEventKey: { menu: MENU_NAMES.NAVIGATION_DRAWER, action: 'menu_item_settings' },
      name: t(I18N_KEYS.MAIN_DASHBOARD.USERMENU.MENU_ITEM_SETTINGS),
      icon: <SettingsIcon width={20} height={20} />,
      action: () => nav(`/settings?section=${SettingsPageSections.WORKSPACE_SETTINGS}`),
    });

    if (isSubscriptionTypeIn([SubscriptionType.Team, SubscriptionType.Enterprise])) {
      res.push({
        biEventKey: { menu: MENU_NAMES.NAVIGATION_DRAWER, action: 'members' },
        name: t(I18N_KEYS.SETTINGS.MENU.MEMBERS),
        icon: <TeamIcon width={20} height={20} />,
        action: () => nav(`/settings?section=${SettingsPageSections.TEAM}`),
      });
    }

    if (isThemeModeSelectable) {
      res.push(
        {
          type: 'divider' as const,
        },
        {
          type: 'slot' as const,
          el: <ThemeMenuItem lead={<Icon24Sun aria-hidden="true" />} onClose={handleCloseUserMenu} />,
        },
        {
          type: 'divider' as const,
        },
      );
    } else if (isSubscriptionTypeIn([SubscriptionType.Team, SubscriptionType.Enterprise])) {
      res.push({
        type: 'divider' as const,
      });
    }

    res.push({
      biEventKey: { menu: MENU_NAMES.NAVIGATION_DRAWER, action: 'signout' },
      name: t(I18N_KEYS.SETTINGS.MENU.SIGNOUT),
      icon: <SignOutIcon className="wea-menu-icon" />,
      action: signOutUser,
    });
    return res;
  }, [handleCloseUserMenu, isThemeModeSelectable, nav, signOutUser, t, isSubscriptionTypeIn]);

  const handleDiscordClick = () => window.open(EXTERNAL_LINKS.discordInvite);

  const folderMenuItems = useMemo(() => {
    return [
      {
        biEventKey: { menu: MENU_NAMES.NAVIGATION_DRAWER, action: 'new_file' },
        name: t(areTabsEnabled ? I18N_KEYS.NAVIGATION_DRAWER.NEW_WORKFLOW : I18N_KEYS.NAVIGATION_DRAWER.NEW_FILE),
        action: handleCreateFile,
        icon: <FileIcon width={16} height={16} />,
      },
      {
        biEventKey: { menu: MENU_NAMES.NAVIGATION_DRAWER, action: 'new_folder' },
        name: t(I18N_KEYS.NAVIGATION_DRAWER.NEW_FOLDER),
        action: () => setIsShowingCreateFolderDialog(true),
        icon: <FolderEmptyIcon width={16} height={16} />,
      },
    ];
  }, [areTabsEnabled, t, handleCreateFile, setIsShowingCreateFolderDialog]);

  const drawer = (
    <FlexCol sx={{ justifyContent: 'space-between', height: '100%', minHeight: 0 }}>
      <Box data-testid="dashboard-drawer-upper-container" sx={{ minHeight: 0, overflowY: 'auto' }}>
        <FlexCenVer data-testid="dashboard-drawer-user" sx={{ height: '56px', px: 1 }}>
          {isFplMigrationEnabled ? (
            <ButtonPrimitive
              {...stylex.props(
                styles.button,
                Boolean(anchorElUserMenu) && styles.buttonActive,
                !user?.activeWorkspace && styles.buttonDisabled,
              )}
              onClick={handleOpenUserMenu}
              disabled={!user?.activeWorkspace}
              aria-label={name ?? 'User menu'}
              aria-haspopup="menu"
              aria-expanded={Boolean(anchorElUserMenu)}
            >
              <div {...stylex.props(styles.buttonContent)}>
                <span {...stylex.props(styles.avatarWrapper)}>
                  <DrawerUserAvatar displayName={name ?? ''} photoURL={user.photoURL} />
                </span>
                <EllipsisText maxWidth="120px" variant="body-lg-md" style={{ fontWeight: 500 }}>
                  {name}
                </EllipsisText>
              </div>
              <Icon16ChevronDown {...stylex.props(styles.buttonIcon)} />
            </ButtonPrimitive>
          ) : (
            <AppToggleButton
              value="user-menu"
              selected={Boolean(anchorElUserMenu)}
              onClick={handleOpenUserMenu}
              disabled={!user?.activeWorkspace}
              aria-label={name ?? 'User menu'}
              aria-haspopup="menu"
              iconPrefix={<DrawerUserAvatar displayName={name ?? ''} photoURL={user.photoURL} />}
              btnH="40px"
              svgSize="12px"
              sx={{
                display: 'flex',
                gap: 1,
                border: 'none !important',
                opacity: !user?.activeWorkspace ? 0.5 : 1,
                cursor: !user?.activeWorkspace ? 'not-allowed' : 'pointer',
                '& .drawer-user-avatar-fallback': {
                  width: 24,
                  height: 24,
                },
              }}
            >
              <EllipsisText maxWidth="120px" variant="body-std-md">
                {name}
              </EllipsisText>
              <CaretIcon title="caret" />
            </AppToggleButton>
          )}
        </FlexCenVer>
        <FlexCenHorVer sx={{ width: '100%', p: 1, mt: 1, mb: 0, pb: 0 }}>
          <ButtonContained fullWidth onClick={handleCreateFile} startIcon={<PlusIcon width={16} height={16} />}>
            {t(
              areTabsEnabled
                ? I18N_KEYS.NAVIGATION_DRAWER.CREATE_NEW_WORKFLOW
                : I18N_KEYS.NAVIGATION_DRAWER.CREATE_NEW_FILE,
            )}
          </ButtonContained>
        </FlexCenHorVer>
        <Box sx={{ px: 1, py: isFplMigrationEnabled ? 0 : 1, mt: '24px', mb: 2 }}>
          <List>
            {foldersList.map((item) => (
              <NavigationListItem
                key={item.label}
                icon={item.icon}
                label={item.label}
                selected={isItemSelected(item)}
                disabled={item.disable}
                onClick={() => handleListItemClick(item)}
                endIcon={item.endIcon}
                onEndIconClick={handleOpenCreateMenu}
                endIconAriaLabel={t(
                  areTabsEnabled
                    ? I18N_KEYS.NAVIGATION_DRAWER.CREATE_MENU_ARIA_WORKFLOW
                    : I18N_KEYS.NAVIGATION_DRAWER.CREATE_MENU_ARIA,
                )}
                buttonSx={{ height: '40px' }}
              />
            ))}
          </List>
          {/* TODO: Uncomment when we want to return the community models */}
          {/*<Typography component="div" variant="label-sm-rg" sx={{ pt: 3, pb: 0.5, px: 2, opacity: 0.64 }}>*/}
          {/*  {t(I18N_KEYS.NAVIGATION_DRAWER.MODELS_SECTION)}*/}
          {/*</Typography>*/}
          {/*<List sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>*/}
          {/*  {modelsList.map((item) => (*/}
          {/*    <ListItem*/}
          {/*      key={item.label}*/}
          {/*      disablePadding*/}
          {/*      sx={{ background: item.section === selectedSection ? color.Black92 : '' }}*/}
          {/*    >*/}
          {/*      <ListItemButton sx={{ height: '40px' }} onClick={() => handleListItemClick(item.section)}>*/}
          {/*        <ListItemIcon>{item.icon}</ListItemIcon>*/}
          {/*        <ListItemText*/}
          {/*          primary={item.label}*/}
          {/*          sx={{*/}
          {/*            '& .MuiListItemText-primary': {*/}
          {/*              fontWeight: item.section === selectedSection ? 500 : 400,*/}
          {/*            },*/}
          {/*          }}*/}
          {/*        />*/}
          {/*      </ListItemButton>*/}
          {/*    </ListItem>*/}
          {/*  ))}*/}
          {/*</List>*/}
        </Box>
      </Box>
      <Box
        data-testid="dashboard-drawer-bottom-container"
        sx={isFplMigrationEnabled ? { flexShrink: 0, mb: 2 } : { flexShrink: 0, p: 1 }}
      >
        <UniversalUpgradeBadge />
        <List>
          <NavigationListItem
            data-testid="support-link-container"
            icon={<QuestionMarkIcon width={20} height={20} />}
            label={t(I18N_KEYS.NAVIGATION_DRAWER.SUPPORT)}
            onClick={openIntercom}
          />
          <NavigationListItem
            data-testid="discord-link-container"
            icon={<DiscordLogo width={20} height={20} title="discord" />}
            label={t(I18N_KEYS.NAVIGATION_DRAWER.DISCORD_CTA)}
            onClick={handleDiscordClick}
          />
        </List>
      </Box>
    </FlexCol>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      aria-label={t(I18N_KEYS.NAVIGATION_DRAWER.DASHBOARD_NAVIGATION_ARIA)}
      id="dashboard-drawer-wrapper"
    >
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onTransitionEnd={handleDrawerTransitionEnd}
        onClose={handleDrawerClose}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            mt: '64px',
            zIndex: fplTokens.zIndex.sidebar,
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            borderRightColor: fplTokens.color.border,
            width: drawerWidth,
            top: `${offset}px`,
            height: `calc(100vh - ${offset}px)`,
            zIndex: fplTokens.zIndex.sidebar,
          },
        }}
        open
      >
        {drawer}
      </Drawer>
      <UserMenu anchorEl={anchorElUserMenu} onClose={handleCloseUserMenu} settings={menuItems} />
      <AppContextMenu
        open={Boolean(anchorElCreateMenu)}
        onClose={handleCloseCreateMenu}
        mouseX={anchorElCreateMenu ? anchorElCreateMenu.getBoundingClientRect().right - 30 : null}
        mouseY={anchorElCreateMenu ? anchorElCreateMenu.getBoundingClientRect().bottom - 10 : null}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        width="200px"
        items={folderMenuItems}
      />

      <CreateFolderModal
        open={isShowingCreateFolderDialog}
        onClose={() => setIsShowingCreateFolderDialog(false)}
        onConfirm={handleCreateFolder}
      />
    </Box>
  );
}

const styles = stylex.create({
  button: {
    display: 'flex',
    alignItems: 'center',
    height: '32px',
    paddingInline: '4px',
    borderRadius: borderRadius.medium,
    color: color.text,
    cursor: 'pointer',
    overflow: 'hidden',
    backgroundColor: {
      default: null,
      ':hover': color.bgHover,
    },
    outline: {
      default: null,
      ':focus-visible': `1px solid ${color.borderSelected}`,
    },
    outlineOffset: '-1px',
  },
  buttonActive: {
    backgroundColor: color.bgHover,
  },
  buttonDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
    backgroundColor: {
      default: null,
      ':hover': null,
    },
  },
  buttonContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  avatarWrapper: {
    position: 'relative',
    display: 'inline-flex',
    flexShrink: 0,
  },
  buttonIcon: {
    flexShrink: 0,
  },
});

export default NavigationDrawer;
